import React from "react"
import { partOfSpeechGroups } from "./part_of_speech"
import { OptionSelect } from "./option_select"

// Parts of speech grouped by word class (content / function / names), each option teaching with an icon
// + Irish name + blurb, and each group header explaining the category.
const POS_GROUPS = partOfSpeechGroups()

export interface SenseDraft {
  partOfSpeech: string
  definition: string
  translation: string
  // Advanced fields, edited in the modal's expanded/full-screen view via a caller-supplied
  // `renderAdvanced` slot (register, grammar rows, verb conjugation overrides, proper-noun entity type).
  // Opaque to the core form and typed loosely here so this dependency-free package needn't know the
  // grammar shape; they ride along on the draft and are preserved through save.
  register?: string
  grammar?: Record<string, string>
  conjugationForms?: Record<string, unknown>
  /** Proper-noun kind (name/place/…) for a noun sense; "" = common noun. */
  entityType?: string
}

export const EMPTY_SENSE_DRAFT: SenseDraft = {
  partOfSpeech: "",
  definition: "",
  translation: "",
}

/**
 * The sense form fields (part of speech + definition + translation), shared by every create/edit zone —
 * the create modal, the edit modal, a full-screen editor. Controlled: pass `value` + `onChange`. Keeping
 * the fields in one place means create and edit never drift.
 */
export const SenseFields: React.FC<{
  value: SenseDraft
  onChange: (value: SenseDraft) => void
  /** Disable while a mutation is in flight. */
  disabled?: boolean
}> = ({ value, onChange, disabled }) => {
  const set = (patch: Partial<SenseDraft>) => onChange({ ...value, ...patch })

  return (
    <div className="space-y-4">
      <label className="block">
        <span className="text-sm font-medium">Part of speech</span>
        <OptionSelect
          value={value.partOfSpeech}
          onChange={(partOfSpeech) => set({ partOfSpeech })}
          groups={POS_GROUPS}
          disabled={disabled}
          ariaLabel="Part of speech"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium">Definition</span>
        <textarea
          value={value.definition}
          onChange={(e) => set({ definition: e.target.value })}
          disabled={disabled}
          rows={2}
          className="mt-1 block w-full rounded border border-secondary-300 p-2"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium">Translation</span>
        <input
          value={value.translation}
          onChange={(e) => set({ translation: e.target.value })}
          disabled={disabled}
          className="mt-1 block w-full rounded border border-secondary-300 p-2"
        />
      </label>
    </div>
  )
}

/** A sense needs its part of speech (to slot into a POS group) and at least one meaning field. */
export const isSenseDraftValid = (draft: SenseDraft): boolean =>
  !!draft.partOfSpeech &&
  (draft.definition.trim().length > 0 || draft.translation.trim().length > 0)

export default SenseFields
